import { useEffect } from "react";
import { MapPin, Clock, Compass } from "lucide-react";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";
import ContactCards, { CONTACT } from "@/components/ContactCards";

export default function Contact() {
  useEffect(() => {
    document.title = "Contact & Enquiries — Dice International LLC";
  }, []);

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-32 sm:px-10 lg:pt-40" data-testid="contact-hero">
        <Overline testId="contact-overline">Contact the Desk</Overline>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="contact-heading">
          <MaskedLine delay={0.15}>Speak with a broker</MaskedLine>
          <MaskedLine delay={0.3}>
            <em className="text-[#C5A059]">today.</em>
          </MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Email, call or WhatsApp the senior desk in
            Dubai.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 sm:px-10" data-testid="contact-channels-section">
        <ContactCards />
      </section>

      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="office-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <div className="border border-[#1E3A6E]/60 bg-[#091B3E]/70 p-8 backdrop-blur-xl sm:p-10" data-testid="office-info-panel">
                  <Overline testId="office-info-overline">Headquarters</Overline>
                  <div className="mt-8 space-y-8">
                    <div className="flex gap-4">
                      <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#C5A059]" strokeWidth={1.5} />
                      <div>
                        <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">Office</p>
                        <p className="mt-2 text-base leading-relaxed text-white">{CONTACT.address}</p>
                        <a
                          data-testid="office-directions-link"
                          href={CONTACT.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-block text-sm font-medium text-[#C5A059] underline decoration-[#C5A059]/40 underline-offset-4 transition-colors duration-300 hover:text-[#E2C08D]"
                        >
                          Get directions
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Clock className="mt-1 h-5 w-5 shrink-0 text-[#C5A059]" strokeWidth={1.5} />
                      <div>
                        <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">Hours</p>
                        <p className="mt-2 text-base text-white">Monday – Friday</p>
                        <p className="text-sm text-slate-400">09:00 – 18:00 GST</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Compass className="mt-1 h-5 w-5 shrink-0 text-[#C5A059]" strokeWidth={1.5} />
                      <div>
                        <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">Coordinates</p>
                        <p className="mt-2 font-mono text-sm tracking-wider text-slate-300">25.0760° N · 55.1405° E</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-10 border-t border-[#1E3A6E]/50 pt-8">
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">Direct Lines</p>
                    <div className="mt-4 space-y-3 font-mono text-sm">
                      <a data-testid="office-email-link" href={`mailto:${CONTACT.email}`} className="block text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]">
                        {CONTACT.email}
                      </a>
                      <a data-testid="office-phone-link" href={`tel:${CONTACT.phoneRaw}`} className="block text-slate-300 transition-colors duration-300 hover:text-white">
                        {CONTACT.phoneDisplay}
                      </a>
                      <a data-testid="office-whatsapp-link" href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="block text-slate-300 transition-colors duration-300 hover:text-white">
                        WhatsApp {CONTACT.phoneDisplay}
                      </a>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
